import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import { adminAPI } from '../config/api';
import { showAppToast } from '../utils/notifications';

const FULL_ACCESS = 'platform.full_access';

const fallbackPolicy = {
  roles: [
    { id: 'admin', label: 'Super Admin', permissions: [FULL_ACCESS] },
    { id: 'fleet_manager', label: 'Fleet Manager', permissions: ['fleet.manage', 'categories.manage', 'analytics.view'] },
    { id: 'reservations_manager', label: 'Reservations Manager', permissions: ['bookings.manage', 'fleet.manage', 'analytics.view'] },
    { id: 'support_agent', label: 'Support Agent', permissions: ['messages.manage', 'bookings.manage'] },
    { id: 'account_manager', label: 'Account Manager', permissions: ['users.manage', 'bookings.manage', 'analytics.view'] },
    { id: 'user', label: 'Customer', permissions: [] },
  ],
  permissions: [
    { id: FULL_ACCESS, label: 'Full platform access' },
    { id: 'bookings.manage', label: 'Booking management' },
    { id: 'fleet.manage', label: 'Vehicle and fleet management' },
    { id: 'categories.manage', label: 'Fleet category management' },
    { id: 'users.manage', label: 'User, account, and access control' },
    { id: 'messages.manage', label: 'Support and message management' },
    { id: 'analytics.view', label: 'Analytics and reporting' },
    { id: 'settings.manage', label: 'Platform settings' },
  ],
};

function displayName(user) {
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unnamed account';
}

function roleLabel(policy, role) {
  return policy.roles.find((item) => item.id === role)?.label || role.replaceAll('_', ' ');
}

function getRolePreset(policy, role) {
  return [...(policy.roles.find((item) => item.id === role)?.permissions || [])];
}

function hasFullAccess(permissions) {
  return permissions.includes(FULL_ACCESS);
}

function normalizePermissions(value) {
  return Array.isArray(value) ? value : [];
}

function AdminAccess() {
  const [users, setUsers] = useState([]);
  const [policy, setPolicy] = useState(fallbackPolicy);
  const [drafts, setDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAccessData = async () => {
    setLoading(true);
    try {
      const [policyResponse, usersResponse] = await Promise.all([
        adminAPI.getAccessPolicy(),
        adminAPI.getUsers(),
      ]);
      const nextPolicy = {
        roles: policyResponse.data?.roles?.length ? policyResponse.data.roles : fallbackPolicy.roles,
        permissions: policyResponse.data?.permissions?.length ? policyResponse.data.permissions : fallbackPolicy.permissions,
      };
      const nextUsers = usersResponse.data || [];
      setPolicy(nextPolicy);
      setUsers(nextUsers);
      setDrafts(Object.fromEntries(nextUsers.map((user) => [
        user.id,
        {
          role: user.role || 'user',
          permissions: normalizePermissions(user.permissions),
        },
      ])));
    } catch (error) {
      console.error('Failed to load access controls:', error);
      showAppToast({ message: 'Failed to load access controls.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccessData();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return users.filter((user) => (
      (roleFilter === 'all' || (drafts[user.id]?.role || user.role || 'user') === roleFilter) &&
      (!term || displayName(user).toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        roleLabel(policy, drafts[user.id]?.role || user.role || 'user').toLowerCase().includes(term))
    ));
  }, [drafts, policy, roleFilter, searchTerm, users]);

  const accessStats = useMemo(() => {
    const privilegedUsers = users.filter((user) => (user.role || 'user') !== 'user');
    const fullAccessUsers = users.filter((user) => hasFullAccess(normalizePermissions(user.permissions)));
    const activeScopes = new Set(users.flatMap((user) => normalizePermissions(user.permissions)));
    return [
      { label: 'Privileged accounts', value: privilegedUsers.length, icon: 'bi-shield-check' },
      { label: 'Full access', value: fullAccessUsers.length, icon: 'bi-stars' },
      { label: 'Customer accounts', value: users.length - privilegedUsers.length, icon: 'bi-person' },
      { label: 'Active scopes', value: activeScopes.size, icon: 'bi-diagram-3' },
    ];
  }, [users]);

  const roleCounts = useMemo(() => {
    const counts = new Map();
    users.forEach((user) => {
      const role = user.role || 'user';
      counts.set(role, (counts.get(role) || 0) + 1);
    });
    return counts;
  }, [users]);

  const updateDraft = (userId, nextDraft) => {
    setDrafts((current) => ({
      ...current,
      [userId]: {
        ...current[userId],
        ...nextDraft,
      },
    }));
  };

  const isDirty = (user) => {
    const draft = drafts[user.id];
    if (!draft) return false;
    const savedPermissions = normalizePermissions(user.permissions).slice().sort();
    const draftPermissions = normalizePermissions(draft.permissions).slice().sort();
    return draft.role !== (user.role || 'user') || JSON.stringify(savedPermissions) !== JSON.stringify(draftPermissions);
  };

  const resetDraft = (user) => {
    updateDraft(user.id, {
      role: user.role || 'user',
      permissions: normalizePermissions(user.permissions),
    });
  };

  const changeRole = (userId, role) => {
    updateDraft(userId, {
      role,
      permissions: getRolePreset(policy, role),
    });
  };

  const togglePermission = (userId, scope) => {
    const current = drafts[userId] || { role: 'user', permissions: [] };
    const permissions = normalizePermissions(current.permissions);
    const nextPermissions = permissions.includes(scope)
      ? permissions.filter((item) => item !== scope)
      : [...permissions, scope];

    updateDraft(userId, {
      permissions: scope === FULL_ACCESS && !permissions.includes(scope)
        ? [FULL_ACCESS]
        : nextPermissions.filter((item) => scope === FULL_ACCESS || item !== FULL_ACCESS),
    });
  };

  const saveAccess = async (user) => {
    const draft = drafts[user.id] || { role: user.role || 'user', permissions: normalizePermissions(user.permissions) };
    setSavingId(user.id);
    try {
      await adminAPI.updateUser(user.id, {
        role: draft.role,
        permissions: draft.role === 'user' ? [] : normalizePermissions(draft.permissions),
      });
      showAppToast({ message: 'Access updated successfully', type: 'success', duration: 2400 });
      await loadAccessData();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to update access', type: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
        <div className="mx-auto max-w-7xl">
          <div className="admin-header admin-access-header">
            <div>
              <p className="text-accent">People &amp; permissions</p>
              <h1 className="section-title text-4xl text-primary-theme">Access control</h1>
              <p className="text-secondary-theme">
                Assign roles and fine-tune what each team member can view or manage.
              </p>
            </div>
            <label className="admin-search-field">
              <i className="bi bi-search" aria-hidden="true" />
              <span className="sr-only">Search accounts or roles</span>
              <input
                type="search"
                placeholder="Search accounts or roles"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="admin-search compact"
              />
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm('')} aria-label="Clear search">
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              )}
            </label>
          </div>

          <section className="admin-access-stats" aria-label="Access summary">
            {accessStats.map((stat) => (
              <article key={stat.label} className="admin-stat-card">
                <div>
                  <p>{stat.label}</p>
                  <strong>{stat.value}</strong>
                </div>
                <i className={`bi ${stat.icon}`} />
              </article>
            ))}
          </section>

          <section className="admin-access-role-grid" aria-label="Filter accounts by role">
            <button
              type="button"
              className={`admin-access-role-card ${roleFilter === 'all' ? 'active' : ''}`}
              onClick={() => setRoleFilter('all')}
              aria-pressed={roleFilter === 'all'}
            >
              <div><span>{users.length}</span><strong>All accounts</strong></div>
              <p>Every role</p>
            </button>
            {policy.roles.map((role) => (
              <button
                type="button"
                key={role.id}
                className={`admin-access-role-card ${roleFilter === role.id ? 'active' : ''}`}
                onClick={() => setRoleFilter((current) => current === role.id ? 'all' : role.id)}
                aria-pressed={roleFilter === role.id}
              >
                <div>
                  <span>{roleCounts.get(role.id) || 0}</span>
                  <strong>{role.label}</strong>
                </div>
                <p>{role.permissions.length ? `${role.permissions.length} scope${role.permissions.length === 1 ? '' : 's'}` : 'Portal account'}</p>
              </button>
            ))}
          </section>

          <section className="admin-access-board card-luxury">
            <div className="admin-card-header">
              <div>
                <p className="text-accent">Accounts</p>
                <h2 className="section-title text-2xl text-primary-theme">Roles and permissions</h2>
                <span className="admin-card-subtitle">Showing {filteredUsers.length} of {users.length} accounts</span>
              </div>
            </div>

            {loading ? (
              <div className="admin-booking-empty">Loading access controls...</div>
            ) : (
              <div className="admin-access-list">
                {filteredUsers.map((user) => {
                  const draft = drafts[user.id] || { role: user.role || 'user', permissions: normalizePermissions(user.permissions) };
                  const permissions = normalizePermissions(draft.permissions);
                  return (
                    <article key={user.id} className="admin-access-account">
                      <div className="admin-access-account-header">
                        <div className="admin-access-account-main">
                          <div className="admin-access-avatar" aria-hidden="true">
                            {displayName(user).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <strong>{displayName(user)}</strong>
                            <span title={user.email}>{user.email}</span>
                          </div>
                        </div>
                        <div className="admin-access-account-status">
                          {isDirty(user) && <span><i className="bi bi-circle-fill" aria-hidden="true" /> Unsaved changes</span>}
                          <small>Account #{user.id}</small>
                        </div>
                      </div>

                      <div className="admin-access-account-body">
                        <label className="admin-access-role-select">
                          <span>Assigned role</span>
                          <select
                            value={draft.role}
                            onChange={(event) => changeRole(user.id, event.target.value)}
                          >
                            {policy.roles.map((role) => (
                              <option key={role.id} value={role.id}>{role.label}</option>
                            ))}
                          </select>
                          <small>Changing the role applies its recommended permission preset.</small>
                        </label>

                        <fieldset className="admin-access-permission-fieldset">
                          <legend>Permission scopes</legend>
                          <div className="admin-access-permissions" aria-label={`${displayName(user)} permissions`}>
                            {policy.permissions.map((permission) => {
                              const checked = permissions.includes(permission.id);
                              const fullAccessActive = hasFullAccess(permissions) && permission.id !== FULL_ACCESS;
                              return (
                                <label key={permission.id} className={`admin-access-scope ${checked || fullAccessActive ? 'active' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked || fullAccessActive}
                                    disabled={draft.role === 'user' || fullAccessActive}
                                    onChange={() => togglePermission(user.id, permission.id)}
                                  />
                                  <span>{permission.label}</span>
                                </label>
                              );
                            })}
                          </div>
                          {draft.role === 'user' && <small>Customer accounts use the customer portal and do not receive admin scopes.</small>}
                        </fieldset>
                      </div>

                      <footer className="admin-access-account-actions">
                        <button type="button" className="btn-luxury-outline" disabled={!isDirty(user) || savingId === user.id} onClick={() => resetDraft(user)}>
                          Reset changes
                        </button>
                        <button
                          type="button"
                          className="btn-luxury admin-access-save"
                          disabled={!isDirty(user) || savingId === user.id}
                          onClick={() => saveAccess(user)}
                        >
                          <i className="bi bi-shield-lock" aria-hidden="true" />
                          {savingId === user.id ? 'Saving…' : 'Save access'}
                        </button>
                      </footer>
                    </article>
                  );
                })}

                {!filteredUsers.length && (
                  <div className="admin-booking-empty">No accounts match the current search.</div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default AdminAccess;
